/* MessageCenterV3.js — Slack-style department channels for VOS
 * ─────────────────────────────────────────────────────────────
 * Built May 15, 2026 to founder's mockup spec:
 *   • Left rail: cross-project channels (#all-company etc.) + per-project departments
 *   • Top bar:   project picker (switches the department channel set)
 *   • Center:    message thread with @mentions + file attachments
 *   • Right rail: project / channel members (privacy-filtered, collapsible on mobile)
 *
 * Privacy (enforced server-side, mirrored in UI):
 *   • Clients see ONLY their per-project #client-room. Members list shows
 *     only master_admin + production_manager(s).
 *   • Artists / vendors / employees never see clients in any list / mention
 *     dropdown, and cannot see the #client-room at all.
 *
 * Realtime via the existing /api/ws/notifications socket — incoming
 * "channel_message" events with our channel_id auto-append to the thread.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import useRealtime from '@/hooks/useRealtime';
import {
  Hash, Lock, Send, Paperclip, AtSign, Smile, Search, Plus, Users,
  ChevronLeft, ChevronRight, Settings, FileText, Image as ImageIcon,
  MoreHorizontal, Bell, X, Menu, Megaphone, Calendar, AlertCircle,
  CheckCircle2, Clock, Edit2, Trash2, Pin, Check, Volume2, VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';

const HANDLE_FROM_EMAIL = (email = '') => (email.split('@')[0] || '').toLowerCase();

// ───── Helper formatters ─────
const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ''; }
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return new Date(a).toDateString() === new Date(b).toDateString();
};

const formatDayHeader = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

// Render message body — replaces @handles with styled spans. Telegram-style:
// only the handle is visible, no underlying email/name leaked.
function renderBody(text) {
  if (!text) return null;
  const parts = text.split(/(@[A-Za-z0-9][A-Za-z0-9._-]{1,40})/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return (
        <span key={i} className="text-indigo-300 bg-indigo-500/10 px-1 rounded font-medium">
          {p}
        </span>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ───── Channel sidebar item ─────
function ChannelRow({ channel, active, onSelect }) {
  const Icon = channel.kind === 'cross' && channel.slug === 'announcements'
    ? Megaphone
    : channel.kind === 'client-room' ? Lock : Hash;
  return (
    <button
      onClick={onSelect}
      data-testid={`mc-channel-${channel.slug}`}
      className={`group w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[13px] text-left transition-colors
        ${active
          ? 'bg-indigo-500/20 text-indigo-100 border-l-2 border-indigo-400'
          : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 border-l-2 border-transparent'}`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{channel.label}</span>
      </span>
      {channel.unread > 0 && (
        <span className="bg-indigo-500 text-white text-[10px] font-bold rounded-full px-1.5 py-px shrink-0">
          {channel.unread > 99 ? '99+' : channel.unread}
        </span>
      )}
    </button>
  );
}

// ───── Member rail row ─────
function MemberRow({ member, currentUserId }) {
  const handle = HANDLE_FROM_EMAIL(member.email);
  const isAdmin = member.role === 'master_admin';
  const isPM = member.role === 'production_manager';
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-800/40 rounded-md">
      <div className="relative">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
          {(member.full_name || handle).charAt(0).toUpperCase()}
        </div>
        <span className="absolute -bottom-px -right-px w-2 h-2 rounded-full bg-emerald-500 border border-zinc-900" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-zinc-200 truncate flex items-center gap-1.5">
          @{handle}
          {member.id === currentUserId && <span className="text-[9px] text-zinc-500">(you)</span>}
        </div>
        <div className="text-[10px] text-zinc-500 truncate">
          {isAdmin ? 'Founder / CEO' : isPM ? 'Production Manager'
            : member.role?.replace(/_/g, ' ')}
        </div>
      </div>
    </div>
  );
}

// ───── Mention autocomplete dropdown ─────
function MentionDropdown({ items, onPick, activeIdx }) {
  if (!items.length) return null;
  return (
    <div className="absolute bottom-full mb-1 left-0 w-72 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl z-30 max-h-60 overflow-y-auto"
         data-testid="mc-mention-dropdown">
      {items.map((u, i) => (
        <button
          key={u.id}
          type="button"
          data-testid={`mc-mention-${HANDLE_FROM_EMAIL(u.email)}`}
          onClick={() => onPick(u)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
            ${i === activeIdx ? 'bg-indigo-500/20' : 'hover:bg-zinc-800/70'}`}
        >
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center text-[10px] font-bold text-white">
            {(u.full_name || u.email).charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-zinc-100 truncate">@{HANDLE_FROM_EMAIL(u.email)}</div>
            <div className="text-[10px] text-zinc-500 truncate">{u.full_name || u.role}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ───── Sound mute toggle (founder ask Feb 2026) ─────
// Reads/writes localStorage key 'vos_mute_chat_sound' through the global
// helpers set by MessageSoundProvider. Stays in sync via a custom event
// so a toggle elsewhere reflects here too.
function ChatSoundMuteToggle() {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem('vos_mute_chat_sound') === '1'; } catch { return false; }
  });
  useEffect(() => {
    const onChange = (e) => setMuted(!!e.detail?.muted);
    window.addEventListener('vos:mute-changed', onChange);
    return () => window.removeEventListener('vos:mute-changed', onChange);
  }, []);
  const click = () => {
    if (typeof window.__vosToggleMute === 'function') {
      const next = window.__vosToggleMute();
      setMuted(next);
    }
  };
  return (
    <button
      onClick={click}
      title={muted ? 'Unmute chat sound' : 'Mute chat sound'}
      data-testid="mc-mute-toggle"
      className={`h-9 px-2.5 rounded-md flex items-center gap-1.5 text-[12px] transition-colors border ${
        muted
          ? 'bg-rose-500/15 text-rose-300 border-rose-500/40 hover:bg-rose-500/25'
          : 'text-zinc-400 hover:bg-zinc-800 border-transparent hover:text-zinc-200'
      }`}
    >
      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      <span className="hidden sm:inline">{muted ? 'Muted' : 'Sound on'}</span>
    </button>
  );
}

// ───── Main page ─────
export default function MessageCenterV3() {
  const { user, api } = useAuth();

  // Projects + selected
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  // Channels + selected
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(null);
  // Active channel object (derived)
  const activeChannel = useMemo(
    () => channels.find(c => c.id === channelId) || null, [channels, channelId]);
  // Messages
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  // Members
  const [members, setMembers] = useState([]);
  // Composer
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  // Mention autocomplete
  const [mentionQuery, setMentionQuery] = useState(null); // string or null
  const [mentionItems, setMentionItems] = useState([]);
  const [mentionIdx, setMentionIdx] = useState(0);
  // UI state — left rail collapsed on mobile.
  // Founder ask May 16, 2026: right rail no longer shows the live member
  // list. Instead it surfaces a Project Info panel (deadline + important
  // notifications). Member list is now reachable on demand via the people
  // icon on the channel header — useful but not always-visible.
  const [showLeft, setShowLeft] = useState(true);
  const [showMembers, setShowMembers] = useState(false);
  // Channel topic — small editable note next to the channel name
  // (founder ask May 16, 2026 — used for routing rules like
  // "Tag @bg for BG, @rig for rigging, @props for props").
  const [channelTopic, setChannelTopic] = useState({ topic: '', can_edit: false });
  // Thread panel state — when set, the right rail shows replies for
  // this specific parent message (founder ask May 16, 2026).
  const [openThread, setOpenThread] = useState(null);
  // Founder ask May 17, 2026: switch to Telegram/WhatsApp-style inline
  // replies. `replyingTo` holds the parent message the user is replying
  // to — it's shown as a chip above the main composer and sent with the
  // next message as `reply_to_id`. The right-side ThreadPanel is retired.
  const [replyingTo, setReplyingTo] = useState(null);
  // Pinned-messages drawer (founder ask May 16, 2026).
  const [showPinned, setShowPinned] = useState(false);
  const [pinnedList, setPinnedList] = useState([]);

  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const scrollRef = useRef(null);

  // ── Load projects on mount ──
  useEffect(() => {
    api.get('/projects').then(r => {
      const list = r.data || [];
      setProjects(list);
      // For a client, auto-pick their first project so the client-room is visible.
      if (!projectId && list.length > 0) setProjectId(list[0].id);
    }).catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load channels whenever project changes ──
  // Note: channelId is intentionally NOT in the deps below — we read it
  // through a ref inside the callback. Including channelId would create a
  // loop: load → setChannelId (auto-pick) → callback recreates → re-load.
  const channelIdRef = useRef(channelId);
  useEffect(() => { channelIdRef.current = channelId; }, [channelId]);

  // Monotonic request token — guards against stale fetches landing after a
  // newer one (classic race on first mount when projectId flips from '' to
  // the auto-selected project; the empty fetch can resolve LAST and wipe
  // the 13 channels returned by the project-scoped fetch).
  const channelsReqId = useRef(0);

  useEffect(() => {
    const seq = ++channelsReqId.current;
    const params = projectId ? { project_id: projectId } : {};
    api.get('/messaging/channels', { params })
      .then(r => {
        if (seq !== channelsReqId.current) return;   // stale — ignore
        const list = r.data?.channels || [];
        setChannels(list);
        const currentId = channelIdRef.current;
        if (list.length > 0 && (!currentId || !list.find(c => c.id === currentId))) {
          const isClient = ['client_producer', 'client_director'].includes(user?.role);
          const first = isClient
            ? list.find(c => c.kind === 'client-room') || list[0]
            : list.find(c => c.kind === 'cross' && c.slug === 'general')
              || list.find(c => c.kind === 'department')
              || list[0];
          setChannelId(first?.id || null);
        }
      })
      .catch(() => {
        if (seq !== channelsReqId.current) return;
        setChannels([]);
      });
  }, [api, projectId, user]);

  // ── Load messages whenever channel changes ──
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    setLoadingMsgs(true);
    api.get(`/messaging/channels/${encodeURIComponent(channelId)}/messages`)
      .then(r => { if (!cancelled) setMessages(r.data?.messages || []); })
      .catch(() => { if (!cancelled) setMessages([]); })
      .finally(() => { if (!cancelled) setLoadingMsgs(false); });
    return () => { cancelled = true; };
  }, [api, channelId]);

  // ── Load members for the active channel ──
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    api.get(`/messaging/channels/${encodeURIComponent(channelId)}/members`)
      .then(r => { if (!cancelled) setMembers(r.data?.members || []); })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [api, channelId]);

  // ── Load channel topic — founder ask May 16, 2026 ──
  useEffect(() => {
    if (!channelId) { setChannelTopic({ topic: '', can_edit: false }); return; }
    let cancelled = false;
    api.get(`/messaging/channels/${encodeURIComponent(channelId)}/topic`)
      .then(r => { if (!cancelled) setChannelTopic(r.data || { topic: '', can_edit: false }); })
      .catch(() => { if (!cancelled) setChannelTopic({ topic: '', can_edit: false }); });
    return () => { cancelled = true; };
  }, [api, channelId]);

  // ── Load pinned messages count badge whenever channel changes ──
  const refreshPinned = useCallback(async () => {
    if (!channelId) { setPinnedList([]); return; }
    try {
      const r = await api.get(`/messaging/channels/${encodeURIComponent(channelId)}/pinned`);
      setPinnedList(r.data?.pinned || []);
    } catch {
      setPinnedList([]);
    }
  }, [api, channelId]);
  useEffect(() => { refreshPinned(); }, [refreshPinned]);

  // When the channel changes, close any open thread (it belongs to the
  // previous channel) and any pinned drawer.
  useEffect(() => { setOpenThread(null); setShowPinned(false); setReplyingTo(null); }, [channelId]);

  // ── Mark channel as read whenever the user opens it ──
  // Bug fix May 18, 2026: unread badge ("1") was sticking on a channel
  // even after the user clicked into it and viewed the messages. The
  // backend already exposes POST /channels/{id}/read which stamps
  // channel_seen_v3.last_seen_at — we just never called it. Now we do:
  //   1. Optimistically clear the local unread count so the badge
  //      disappears instantly (no waiting for next channels-refresh).
  //   2. Persist server-side so the badge stays cleared on reload.
  useEffect(() => {
    if (!channelId) return;
    // Optimistic UI: zero out the badge for this channel right away.
    setChannels(prev => prev.map(c =>
      c.id === channelId ? { ...c, unread: 0 } : c));
    // Persist on the server (fire-and-forget — UI already updated).
    api.post(`/messaging/channels/${encodeURIComponent(channelId)}/read`)
      .catch(() => { /* badge stays cleared locally; will retry on next open */ });
  }, [api, channelId]);

  // Founder ask Feb 2026: expose the active channel id globally so the
  // app-wide MessageSoundProvider can suppress the ding when the user is
  // staring at the channel with a focused tab (= no need to alert).
  useEffect(() => {
    window.__vosActiveChannelId = channelId || null;
    return () => { window.__vosActiveChannelId = null; };
  }, [channelId]);

  // Deep-link support: /messages?channel=<id> from the OS notification
  // click — jump straight to the channel the notification was about.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const target = sp.get('channel');
      if (target && !channelId) setChannelId(target);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-scroll to bottom on new messages ──
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, channelId]);

  // ── Realtime: append/update/delete messages for the active channel ──
  // Founder ask May 16, 2026: chat now supports edit/delete/react +
  // channel topic — every mutation broadcasts its own event type so
  // every connected client stays in sync.
  const handleRealtime = useCallback((event) => {
    if (!event || !event.channel_id) return;
    if (event.type === 'channel_message' && event.channel_id === channelId) {
      const incoming = event.message;
      // Thread reply → don't append to main feed, just bump parent's
      // counter so the "N replies" indicator updates live.
      if (incoming?.reply_to_id) {
        setMessages(prev => prev.map(m =>
          m.id === incoming.reply_to_id
            ? { ...m, thread_reply_count: (m.thread_reply_count || 0) + 1,
                last_reply_at: incoming.created_at }
            : m));
        // If the user is currently viewing this thread, append the reply.
        // De-dupe by id — the author's own sendReply optimistically inserts
        // via onAppendReply, and the WS event fires for the same row. Without
        // this guard React logs "duplicate key" warnings for one frame.
        if (openThread && openThread.parent?.id === incoming.reply_to_id) {
          setOpenThread(prev => {
            const existing = prev.replies || [];
            if (existing.some(r => r.id === incoming.id)) return prev;
            return { ...prev, replies: [...existing, incoming] };
          });
        }
        return;
      }
      setMessages(prev => {
        if (prev.some(m => m.id === incoming?.id)) return prev;
        return [...prev, incoming];
      });
    } else if (event.type === 'channel_message') {
      // Bug fix May 18, 2026: if the message arrives in the channel
      // the user is currently looking at, don't bump the unread badge
      // (and also re-stamp last_seen on the server so it stays clean).
      if (event.channel_id === channelId) {
        api.post(`/messaging/channels/${encodeURIComponent(event.channel_id)}/read`)
          .catch(() => {});
        return;
      }
      setChannels(prev => prev.map(c =>
        c.id === event.channel_id ? { ...c, unread: (c.unread || 0) + 1 } : c));
    } else if (event.type === 'channel_message_updated' && event.channel_id === channelId) {
      setMessages(prev => prev.map(m =>
        m.id === event.message?.id ? { ...m, ...event.message } : m));
      // Also update inside the open thread if relevant
      if (openThread) {
        setOpenThread(prev => ({
          ...prev,
          parent: prev.parent?.id === event.message?.id ? { ...prev.parent, ...event.message } : prev.parent,
          replies: (prev.replies || []).map(r => r.id === event.message?.id ? { ...r, ...event.message } : r),
        }));
      }
    } else if (event.type === 'channel_message_deleted' && event.channel_id === channelId) {
      setMessages(prev => prev.map(m =>
        m.id === event.message_id ? { ...m, deleted_at: event.timestamp } : m));
    } else if (event.type === 'channel_message_reaction' && event.channel_id === channelId) {
      setMessages(prev => prev.map(m =>
        m.id === event.message_id ? { ...m, reactions: event.reactions } : m));
      if (openThread) {
        setOpenThread(prev => ({
          ...prev,
          parent: prev.parent?.id === event.message_id ? { ...prev.parent, reactions: event.reactions } : prev.parent,
          replies: (prev.replies || []).map(r => r.id === event.message_id ? { ...r, reactions: event.reactions } : r),
        }));
      }
    } else if (event.type === 'channel_topic_updated' && event.channel_id === channelId) {
      setChannelTopic(prev => ({ ...prev, topic: event.topic, updated_at: event.timestamp }));
    } else if (event.type === 'channel_message_pinned' && event.channel_id === channelId) {
      // Refresh pinned list + flip the per-message pinned flag in the feed
      setMessages(prev => prev.map(m =>
        m.id === event.message_id ? { ...m, pinned: event.pinned } : m));
      refreshPinned();
    }
  }, [channelId, openThread, refreshPinned]);
  useRealtime(handleRealtime);

  // ── Send a message ──
  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text && pendingAttachments.length === 0) return;
    if (sending) return;
    setSending(true);
    try {
      const payload = {
        body: text,
        attachments: pendingAttachments,
      };
      // Telegram-style inline reply (founder ask May 17, 2026): if the
      // user clicked "Reply" on a message, attach reply_to_id so the
      // backend links the new row to its parent and rebroadcasts with
      // the parent quote attached for everyone in the channel.
      if (replyingTo?.id) payload.reply_to_id = replyingTo.id;
      const r = await api.post(`/messaging/channels/${encodeURIComponent(channelId)}/messages`, payload);
      const msg = r.data?.message;
      if (msg) setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      setDraft('');
      setPendingAttachments([]);
      setReplyingTo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  }, [api, channelId, draft, pendingAttachments, sending, replyingTo]);

  // ── @mention autocomplete tracking ──
  const onDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    // Find the @token under the caret. We don't need exact caret position —
    // the last @ followed by non-space chars is good enough for the dropdown.
    const m = v.slice(0, e.target.selectionStart).match(/@([A-Za-z0-9._-]*)$/);
    if (m) {
      const q = m[1];
      setMentionQuery(q);
      api.get('/messaging/users/search', {
        params: { q, project_id: projectId || '' },
      }).then(r => {
        setMentionItems(r.data?.users || []);
        setMentionIdx(0);
      }).catch(() => setMentionItems([]));
    } else {
      setMentionQuery(null);
      setMentionItems([]);
    }
  };

  const pickMention = (u) => {
    const handle = HANDLE_FROM_EMAIL(u.email);
    const el = composerRef.current;
    if (!el) return;
    const caret = el.selectionStart || draft.length;
    const before = draft.slice(0, caret);
    const after = draft.slice(caret);
    const replaced = before.replace(/@([A-Za-z0-9._-]*)$/, `@${handle} `);
    const next = replaced + after;
    setDraft(next);
    setMentionQuery(null);
    setMentionItems([]);
    // Restore caret after the inserted handle
    setTimeout(() => {
      const pos = replaced.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const onComposerKeyDown = (e) => {
    if (mentionItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIdx(i => Math.min(i + 1, mentionItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIdx(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(mentionItems[mentionIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        setMentionItems([]);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Hoisted up from below (used by composer + paste handler in the
  // useCallback dep array — must declare BEFORE those callbacks or
  // we hit "Cannot access 'postingLocked' before initialization"
  // at component mount. Bug fix Feb 11 2026.)
  const postingLocked = activeChannel?.posting_locked;

  // ── File attachments ──
  // Single upload helper used by BOTH the attach button (file picker)
  // AND the clipboard paste handler (founder ask Feb 11 2026 — paste
  // a screen-grab directly into the composer instead of saving →
  // attach). Returns true if the file was uploaded + attached.
  const uploadFile = useCallback(async (f, { silentLargeReject = false } = {}) => {
    if (!f) return false;
    // Founder ask May 16, 2026: bump to 100MB to accommodate
    // turntable / playblast / dailies WIPs shared inline.
    if (f.size > 100 * 1024 * 1024) {
      if (!silentLargeReject) toast.error(`${f.name} is over 100MB — too large.`);
      return false;
    }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('channel_id', channelId);
    try {
      const r = await api.post('/messaging/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPendingAttachments(prev => [...prev, r.data]);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to upload ${f.name}`);
      return false;
    }
  }, [api, channelId]);

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    for (const f of files) await uploadFile(f);
  };

  // Founder ask Feb 11 2026: paste a screen-grab directly into the
  // composer. Reads `event.clipboardData.items`, finds image entries,
  // converts to File (synthesised filename with timestamp), pipes
  // through the same upload helper as the attach button. Plain text
  // pastes fall through to the textarea's native handler.
  const onComposerPaste = useCallback(async (e) => {
    if (!channelId || postingLocked) return;  // nothing to attach to
    const items = e.clipboardData?.items || [];
    const imageFiles = [];
    for (const it of items) {
      if (it.kind === 'file' && (it.type || '').startsWith('image/')) {
        const f = it.getAsFile();
        if (f) imageFiles.push(f);
      }
    }
    if (imageFiles.length === 0) return;  // let native paste through

    e.preventDefault();  // we own this paste now — block the data URL
                         // from landing as raw text inside the textarea
    for (const raw of imageFiles) {
      // Clipboard image files come with name "image.png" by default
      // — give them a useful timestamped filename so the channel
      // history is readable later.
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = (raw.type.split('/')[1] || 'png').toLowerCase();
      const renamed = new File(
        [raw],
        `paste-${ts}.${ext}`,
        { type: raw.type, lastModified: raw.lastModified },
      );
      const ok = await uploadFile(renamed);
      if (ok) {
        // Soft toast so the user knows the screenshot was caught —
        // some browsers fire `paste` without any visible UI change
        // until the upload completes.
        toast.success('Image pasted from clipboard', { duration: 1500 });
      }
    }
  }, [channelId, postingLocked, uploadFile]);

  const removeAttachment = (idx) =>
    setPendingAttachments(prev => prev.filter((_, i) => i !== idx));

  // Compute current project name for the bar
  const projectName = projects.find(p => p.id === projectId)?.name || 'Select a project';

  // Group + label channels for the sidebar
  const crossChannels = channels.filter(c => c.kind === 'cross');
  const deptChannels  = channels.filter(c => c.kind === 'department');
  const clientChannel = channels.find(c => c.kind === 'client-room');

  // For the right-rail count + label
  const memberCount = members.length;
  const channelLabel = activeChannel?.label || '';
  const channelIsLocked = activeChannel?.kind === 'client-room';
  // `postingLocked` is hoisted above (near sendMessage) so paste +
  // composer callbacks can reference it. Don't redeclare here.

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] bg-zinc-950 text-zinc-100" data-testid="message-center-v3">
      {/* Top bar: project picker */}
      <div className="h-14 px-3 sm:px-5 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur flex items-center gap-3 shrink-0">
        <button
          onClick={() => setShowLeft(s => !s)}
          className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center text-zinc-400 hover:bg-zinc-800"
          aria-label="Toggle channel list"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 hidden sm:inline">Project</span>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            data-testid="mc-project-picker"
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-[13px] text-zinc-100 max-w-[14rem] sm:max-w-[20rem] truncate focus:border-indigo-400 outline-none"
          >
            <option value="">— No project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ChatSoundMuteToggle />
          <button
            onClick={() => setShowMembers(s => !s)}
            className={`h-9 px-2.5 rounded-md flex items-center gap-1.5 text-[12px] transition-colors
              ${showMembers ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-500/40'
                            : 'text-zinc-400 hover:bg-zinc-800 border border-transparent'}`}
            title="Show channel members"
            data-testid="mc-toggle-members"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">{memberCount} members</span>
          </button>
        </div>
      </div>

      {/* Main 3-column layout */}
      <div className="flex flex-1 min-h-0">
        {/* LEFT: channel rail */}
        <aside
          className={`${showLeft ? 'flex' : 'hidden'} lg:flex flex-col w-64 shrink-0 bg-zinc-900/40 border-r border-zinc-800 overflow-y-auto`}
          data-testid="mc-channel-rail"
        >
          {projectName && (
            <div className="px-3 pt-3 pb-1.5 text-[11px] text-zinc-500 uppercase tracking-wider truncate">
              {projectName}
            </div>
          )}
          {crossChannels.length > 0 && (
            <div className="mb-2">
              <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-600 font-bold">
                Cross-project
              </div>
              <div className="space-y-px px-1">
                {crossChannels.map(c => (
                  <ChannelRow key={c.id} channel={c} active={c.id === channelId} onSelect={() => setChannelId(c.id)} />
                ))}
              </div>
            </div>
          )}
          {deptChannels.length > 0 && (
            <div className="mb-2">
              <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-600 font-bold flex items-center justify-between">
                <span>Departments</span>
                <span className="text-zinc-700">{deptChannels.length}</span>
              </div>
              <div className="space-y-px px-1">
                {deptChannels.map(c => (
                  <ChannelRow key={c.id} channel={c} active={c.id === channelId} onSelect={() => setChannelId(c.id)} />
                ))}
              </div>
            </div>
          )}
          {clientChannel && (
            <div className="mb-2">
              <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-zinc-600 font-bold">
                Client
              </div>
              <div className="space-y-px px-1">
                <ChannelRow channel={clientChannel} active={clientChannel.id === channelId}
                            onSelect={() => setChannelId(clientChannel.id)} />
              </div>
            </div>
          )}
          {channels.length === 0 && (
            <div className="px-4 py-6 text-[11px] text-zinc-600 italic">
              No channels yet — pick a project above.
            </div>
          )}
        </aside>

        {/* CENTER: messages */}
        <main className="flex-1 flex flex-col min-w-0 bg-zinc-950">
          {/* Channel header */}
          <div className="px-4 sm:px-5 border-b border-zinc-800 bg-zinc-900/40 shrink-0">
            <div className="h-14 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {channelIsLocked
                  ? <Lock className="w-4 h-4 text-amber-400 shrink-0" />
                  : <Hash className="w-4 h-4 text-zinc-500 shrink-0" />}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[15px] font-semibold text-zinc-100 truncate">
                      {channelLabel || 'Select a channel'}
                    </span>
                    <ChannelTopicInline
                      topic={channelTopic.topic}
                      canEdit={channelTopic.can_edit}
                      onSave={async (next) => {
                        try {
                          const r = await api.put(`/messaging/channels/${encodeURIComponent(channelId)}/topic`, { topic: next });
                          setChannelTopic(prev => ({ ...prev, ...r.data }));
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Failed to save topic');
                        }
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                    {memberCount} members · Handle-only
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowMembers(s => !s)}
                className="lg:hidden text-zinc-400 hover:text-zinc-100"
                aria-label="Toggle members"
              >
                <Users className="w-4 h-4" />
              </button>
              {pinnedList.length > 0 && (
                <button
                  onClick={() => { setShowPinned(v => !v); setOpenThread(null); }}
                  className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors ${
                    showPinned ? 'bg-amber-500/20 text-amber-200 border border-amber-500/50'
                               : 'text-amber-300 hover:bg-zinc-800 border border-transparent'}`}
                  data-testid="mc-pinned-toggle"
                  title="Show pinned messages"
                >
                  <Pin className="w-3 h-3" /> {pinnedList.length} pinned
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-1" data-testid="mc-messages">
            {loadingMsgs ? (
              <div className="text-center text-zinc-600 text-[11px] py-12">Loading messages…</div>
            ) : messages.length === 0 ? (
              <div className="text-center text-zinc-600 text-[12px] py-12">
                No messages yet. Be the first to post.
              </div>
            ) : (
              messages.map((m, idx) => {
                const prev = messages[idx - 1];
                const showDay = !prev || !sameDay(prev.created_at, m.created_at);
                // Prefer the server-precomputed handle; fall back to deriving
                // from email (older messages stored before this field landed).
                const handle = m.author_handle || HANDLE_FROM_EMAIL(m.author_email || '');
                const stack = prev && prev.author_user_id === m.author_user_id
                              && !showDay
                              && (new Date(m.created_at) - new Date(prev.created_at) < 5 * 60 * 1000);
                return (
                  <React.Fragment key={m.id}>
                    {showDay && (
                      <div className="flex items-center gap-3 my-3">
                        <div className="flex-1 h-px bg-zinc-800" />
                        <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold">
                          {formatDayHeader(m.created_at)}
                        </span>
                        <div className="flex-1 h-px bg-zinc-800" />
                      </div>
                    )}
                    <MessageRow
                      key={`msgrow-${m.id}`}
                      message={m}
                      handle={handle}
                      stack={stack}
                      api={api}
                      viewerId={user?.id}
                      viewerRole={user?.role}
                      onUpdated={(updated) => setMessages(prev =>
                        prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))}
                      onDeleted={(mid) => setMessages(prev =>
                        prev.map(x => x.id === mid ? { ...x, deleted_at: new Date().toISOString() } : x))}
                      onReplyInline={(parentMsg) => {
                        // Founder ask May 17, 2026: inline reply UX. Set the
                        // quote on the main composer; the next send will
                        // attach reply_to_id and the result renders inline.
                        setReplyingTo({
                          id: parentMsg.id,
                          author_name: parentMsg.author_name
                            || parentMsg.author_handle
                            || (parentMsg.author_email || '').split('@')[0],
                          body_snippet: (parentMsg.body || '').slice(0, 180),
                        });
                        // Focus composer
                        setTimeout(() => {
                          const ta = document.querySelector('[data-testid="mc-composer-textarea"]');
                          if (ta) ta.focus();
                        }, 60);
                      }}
                      onPinToggled={() => refreshPinned()}
                    />
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-zinc-800 bg-zinc-900/30 px-3 sm:px-5 py-3 shrink-0">
            {pendingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {pendingAttachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-zinc-800 rounded-md px-2 py-1 text-[11px] text-zinc-300"
                       data-testid={`mc-attachment-${i}`}>
                    <FileText className="w-3 h-3 text-indigo-300" />
                    <span className="truncate max-w-[12rem]">{a.filename}</span>
                    <button onClick={() => removeAttachment(i)} className="text-zinc-500 hover:text-rose-300">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {postingLocked && (
              <div className="text-[11px] text-amber-400/80 mb-2 italic" data-testid="mc-posting-locked">
                {activeChannel?.lock_reason || 'Posting locked — only admin & PM can announce here.'}
              </div>
            )}
            <div className="relative">
              {mentionItems.length > 0 && (
                <MentionDropdown
                  items={mentionItems}
                  activeIdx={mentionIdx}
                  onPick={pickMention}
                />
              )}
              {/* Reply quote chip — Telegram-style. Shows the parent
                  message we're replying to with an X to cancel. */}
              {replyingTo && (
                <div className="mb-2 flex items-start gap-2 bg-emerald-500/10 border border-emerald-500/40 rounded-md px-3 py-2"
                     data-testid="mc-reply-chip">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-emerald-300 font-semibold flex items-center gap-1">
                      ↪ Replying to <span className="font-mono">{replyingTo.author_name}</span>
                    </p>
                    <p className="text-[12px] text-zinc-400 truncate">
                      {replyingTo.body_snippet || '(attachment)'}
                    </p>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="text-zinc-500 hover:text-rose-300 transition-colors"
                    title="Cancel reply"
                    data-testid="mc-reply-chip-cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 focus-within:border-indigo-400 transition-colors">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!channelId || postingLocked}
                  className="text-zinc-500 hover:text-indigo-300 transition-colors disabled:opacity-30"
                  data-testid="mc-attach-button"
                  title="Attach a file"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="mc-file-input"
                />
                <textarea
                  ref={composerRef}
                  value={draft}
                  onChange={onDraftChange}
                  onKeyDown={onComposerKeyDown}
                  onPaste={onComposerPaste}
                  rows={1}
                  disabled={!channelId || postingLocked}
                  placeholder={postingLocked
                    ? (activeChannel?.lock_reason || 'Locked — only admin / PM can post here')
                    : `Message #${activeChannel?.slug || 'channel'}`}
                  className="flex-1 bg-transparent text-[13px] text-zinc-100 placeholder-zinc-600 outline-none resize-none max-h-32 disabled:opacity-50"
                  data-testid="mc-composer-textarea"
                />
                <button
                  onClick={sendMessage}
                  disabled={sending || !channelId || (!draft.trim() && pendingAttachments.length === 0)}
                  className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-30 text-white p-2 rounded-md transition-colors"
                  data-testid="mc-send"
                  title="Send (Enter)"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[10px] text-zinc-600">
                <span className="flex items-center gap-1"><AtSign className="w-3 h-3" /> Mention</span>
                <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attach (max 100MB)</span>
                <span className="hidden sm:inline">Paste screenshots · Enter to send · Shift+Enter for newline</span>
              </div>
            </div>
          </div>
        </main>

        {/* RIGHT: Project Info panel OR Members (toggled) — founder ask
            May 16: replace always-visible member list with a Project Info
            pane showing deadline + notifications. People icon toggles
            members on demand. */}
        <aside
          className={`${showMembers ? 'flex flex-col' : 'hidden'} w-64 shrink-0 bg-zinc-900/40 border-l border-zinc-800 overflow-y-auto`}
          data-testid="mc-member-rail"
        >
          <div className="px-3 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Members</div>
              <div className="text-[14px] font-semibold text-zinc-100">{memberCount}</div>
            </div>
            <button
              onClick={() => setShowMembers(false)}
              className="text-zinc-500 hover:text-zinc-200"
              data-testid="mc-close-members"
              aria-label="Close members"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-1.5">
            {members.length === 0 && (
              <div className="px-4 py-6 text-[11px] text-zinc-600 italic">No members.</div>
            )}
            {members.map(m => (
              <MemberRow key={m.id} member={m} currentUserId={user?.id} />
            ))}
          </div>
        </aside>

        {!showMembers && !showPinned && (
          <ProjectInfoPanel
            api={api}
            project={projects.find(p => p.id === projectId)}
            user={user}
          />
        )}
        {/* ThreadPanel retired May 17, 2026 — replies now render inline
            in the main feed as Telegram-style quote blocks. openThread
            state retained internally for safety but no panel mounts. */}
        {showPinned && (
          <PinnedPanel
            pinned={pinnedList}
            onClose={() => setShowPinned(false)}
            onJump={(messageId) => {
              setShowPinned(false);
              // Best-effort scroll-to-message
              setTimeout(() => {
                const el = document.querySelector(`[data-testid="mc-msg-${messageId}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.classList.add('ring-2', 'ring-amber-400');
                  setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1800);
                }
              }, 50);
            }}
          />
        )}
      </div>
    </div>
  );
}

// ───── Attachment preview ─────
function AttachmentPreview({ attachment, api }) {
  const { storage_key, filename, content_type } = attachment;
  const isImage = (content_type || '').startsWith('image/');
  const isVideo = (content_type || '').startsWith('video/');
  const [imgUrl, setImgUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Fetch the thumbnail (and the same blob URL is reused for the lightbox
  // — no second fetch on click). Founder ask May 16, 2026: images must
  // show a thumbnail in-channel; click → enlarge inline; ESC / X to close.
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let createdUrl = null;
    api.get(`/reviews/media`, {
      params: { key: storage_key },
      responseType: 'blob',
    }).then(r => {
      if (cancelled) return;
      createdUrl = URL.createObjectURL(r.data);
      setImgUrl(createdUrl);
    }).catch(err => {
      // Surface the error so producers know upload landed but display failed.
      console.warn('AttachmentPreview load failed', storage_key, err?.response?.status);
    });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage_key, isImage]);

  // For videos: lazy-load the blob only when lightbox opens (saves bandwidth
  // when the user just scrolls past).
  useEffect(() => {
    if (!isVideo || !lightboxOpen || videoUrl) return;
    let cancelled = false;
    let createdUrl = null;
    api.get(`/reviews/media`, {
      params: { key: storage_key },
      responseType: 'blob',
    }).then(r => {
      if (cancelled) return;
      createdUrl = URL.createObjectURL(r.data);
      setVideoUrl(createdUrl);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage_key, isVideo, lightboxOpen]);

  // ESC closes the lightbox.
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxOpen(false); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll behind the lightbox.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [lightboxOpen]);

  const downloadFile = async (e) => {
    e?.stopPropagation?.();
    try {
      const r = await api.get(`/reviews/media`, { params: { key: storage_key }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download');
    }
  };

  // ─── Inline render ───
  if (isImage) {
    return (
      <>
        <div className="mt-2 max-w-md">
          {imgUrl
            ? (
              <img
                src={imgUrl}
                alt={filename}
                onClick={() => setLightboxOpen(true)}
                className="rounded-md border border-zinc-800 max-h-72 cursor-zoom-in hover:border-indigo-400 transition-colors"
                data-testid={`mc-attachment-thumb-${filename}`}
              />
            )
            : (
              <div className="w-48 h-32 bg-zinc-800 rounded-md flex items-center justify-center text-zinc-600 animate-pulse">
                <ImageIcon className="w-6 h-6" />
              </div>
            )}
          <div className="text-[10px] text-zinc-500 mt-1 truncate flex items-center gap-2">
            <span className="truncate">{filename}</span>
            <button onClick={downloadFile}
                    className="text-zinc-400 hover:text-indigo-300 underline text-[10px]">
              Download
            </button>
          </div>
        </div>
        {lightboxOpen && imgUrl && (
          <Lightbox imgUrl={imgUrl} filename={filename} onClose={() => setLightboxOpen(false)} onDownload={downloadFile} />
        )}
      </>
    );
  }

  if (isVideo) {
    return (
      <>
        <button
          onClick={() => setLightboxOpen(true)}
          className="mt-2 relative flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-800 rounded-md w-72 h-40 hover:border-indigo-400 transition-colors group"
          data-testid={`mc-attachment-video-${filename}`}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-indigo-500/90 flex items-center justify-center group-hover:bg-indigo-400 transition-colors">
              <svg className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
          <div className="absolute bottom-1.5 left-2 text-[10px] text-zinc-300 truncate max-w-[16rem]">
            {filename}
          </div>
        </button>
        {lightboxOpen && (
          <Lightbox videoUrl={videoUrl} filename={filename} onClose={() => setLightboxOpen(false)} onDownload={downloadFile} />
        )}
      </>
    );
  }

  // Non-image / non-video: standard download tile
  return (
    <button
      onClick={downloadFile}
      className="mt-2 flex items-center gap-2.5 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 hover:border-indigo-400 transition-colors max-w-md"
    >
      <FileText className="w-4 h-4 text-rose-400" />
      <div className="text-left min-w-0">
        <div className="text-[12px] text-zinc-200 truncate">{filename}</div>
        <div className="text-[10px] text-zinc-500">Click to download</div>
      </div>
    </button>
  );
}

// ───── Lightbox ─────
// Founder ask May 16, 2026: clicking an inline image opens an enlarged
// version in the SAME session (no new tab) — ESC or X returns to chat.
// Built as a portal-style fixed overlay; auto-closes on backdrop click.
function Lightbox({ imgUrl, videoUrl, filename, onClose, onDownload }) {
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="mc-lightbox"
    >
      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-[12px] text-zinc-200 truncate max-w-[60%]">{filename}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDownload}
            className="text-[11px] text-zinc-300 hover:text-white border border-zinc-700 rounded-md px-2.5 py-1 hover:bg-white/5"
            data-testid="mc-lightbox-download"
          >
            Download
          </button>
          <button
            onClick={onClose}
            className="text-zinc-300 hover:text-white w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center"
            aria-label="Close (Esc)"
            title="Close (Esc)"
            data-testid="mc-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div onClick={e => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] flex items-center justify-center">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={filename}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-md shadow-2xl"
          />
        ) : videoUrl ? (
          <video
            src={videoUrl}
            controls
            autoPlay
            className="max-w-[92vw] max-h-[88vh] rounded-md shadow-2xl bg-black"
          />
        ) : (
          <div className="text-zinc-400 text-[12px]">Loading…</div>
        )}
      </div>

      {/* Hint */}
      <div className="absolute bottom-3 left-0 right-0 text-center text-[10px] text-zinc-500">
        Press <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">Esc</kbd> or click outside to close
      </div>
    </div>
  );
}


/* ─── Project Info Panel ────────────────────────────────────────────
   Replaces the always-visible member rail (founder ask May 16, 2026).
   Shows the active project's deadline, days-remaining countdown, recent
   approval activity, and unread @mentions — the things a busy creative
   lead glances at while keeping a channel open. */
function ProjectInfoPanel({ api, project, user }) {
  const [stats, setStats]   = useState(null);
  const [mentions, setMentions] = useState([]);

  useEffect(() => {
    if (!project?.id) { setStats(null); return; }
    let cancelled = false;
    // Fetch project + small approval pulse
    Promise.all([
      api.get(`/reviews/pending-count?project_id=${project.id}`).catch(() => ({ data: {} })),
      api.get(`/mentions/inbox?limit=5`).catch(() => ({ data: { mentions: [] } })),
    ]).then(([pendRes, mentRes]) => {
      if (cancelled) return;
      setStats(pendRes.data || {});
      setMentions((mentRes.data?.mentions || []).filter(m =>
        !m.project_id || m.project_id === project.id).slice(0, 5));
    });
    return () => { cancelled = true; };
  }, [api, project?.id]);

  const daysToDeadline = useMemo(() => {
    if (!project?.deadline) return null;
    const ms = new Date(project.deadline) - new Date();
    if (isNaN(ms)) return null;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }, [project?.deadline]);

  const isDelivered = project?.status === 'delivered' || !!project?.delivered_on;

  const deadlineColor =
    daysToDeadline == null ? 'text-zinc-500' :
    daysToDeadline < 0  ? 'text-rose-400' :
    daysToDeadline <= 7 ? 'text-amber-400' :
    'text-emerald-400';

  return (
    <aside
      className="hidden xl:flex flex-col w-72 shrink-0 bg-zinc-900/40 border-l border-zinc-800 overflow-y-auto"
      data-testid="mc-project-info-panel"
    >
      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          Project
        </div>
        <div className="text-[15px] font-semibold text-zinc-100 truncate mt-0.5">
          {project?.name || '—'}
        </div>
      </div>

      {/* Deadline pulse — a DELIVERED project stops counting days (founder
          ask Jun 2026: "otherwise in chat section it will keep counting days") */}
      <div className="px-4 py-3 border-b border-zinc-800/60">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
          <Calendar className="w-3 h-3" /> {isDelivered ? 'Delivery' : 'Deadline'}
        </div>
        {isDelivered ? (
          <div data-testid="mc-project-delivered">
            <div className="text-[13px] text-emerald-300 font-semibold">
              Delivered · {project.delivered_on
                ? new Date(`${String(project.delivered_on).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined,
                    { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Project closed — this channel is archived.</div>
          </div>
        ) : project?.deadline ? (
          <>
            <div className="text-[13px] text-zinc-200">
              {new Date(project.deadline).toLocaleDateString(undefined,
                { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            <div className={`text-[12px] font-semibold mt-0.5 ${deadlineColor}`}>
              {daysToDeadline == null ? '—'
                : daysToDeadline < 0  ? `${Math.abs(daysToDeadline)} days overdue`
                : daysToDeadline === 0 ? 'Due today'
                : `${daysToDeadline} days remaining`}
            </div>
          </>
        ) : (
          <div className="text-[11px] text-zinc-600 italic">No deadline set.</div>
        )}
      </div>

      {/* Shot / completion pulse */}
      {project && (
        <div className="px-4 py-3 border-b border-zinc-800/60">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> Progress
          </div>
          <div className="text-[12px] text-zinc-300">
            {project.completed_shots ?? 0} / {project.shot_count ?? 0} shots completed
          </div>
          {project.shot_count > 0 && (
            <div className="mt-2 w-full bg-zinc-800 rounded-full h-1.5 overflow-hidden">
              <div className="bg-emerald-400 h-full"
                   style={{ width: `${Math.min(100, (project.completed_shots / project.shot_count) * 100)}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Pending approvals */}
      {stats && (stats.mine > 0 || stats.team > 0) && (
        <div className="px-4 py-3 border-b border-zinc-800/60">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Pending Approvals
          </div>
          {stats.mine > 0 && (
            <div className="text-[12px] text-amber-300 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" /> {stats.mine} awaiting your review
            </div>
          )}
          {stats.team > 0 && (
            <div className="text-[11px] text-zinc-400 mt-1">
              {stats.team} in team queue
            </div>
          )}
        </div>
      )}

      {/* Recent @mentions */}
      <div className="px-4 py-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
          <Bell className="w-3 h-3" /> Recent @{HANDLE_FROM_EMAIL(user?.email || '')} mentions
        </div>
        {mentions.length === 0 ? (
          <div className="text-[11px] text-zinc-600 italic">No mentions yet.</div>
        ) : (
          <div className="space-y-2">
            {mentions.map(m => (
              <div key={m.id} className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5"
                   data-testid={`mc-info-mention-${m.id}`}>
                <div className="text-[10px] text-indigo-300 font-medium">
                  @{m.mentioner_name?.split(' ')[0] || 'someone'} · {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="text-[11px] text-zinc-300 line-clamp-2 mt-0.5">
                  {m.snippet}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}



/* ─── Message Row ───────────────────────────────────────────────────
   Replaces the inline bubble JSX with a rich row that supports:
     • Hover toolbar (react · edit · delete) — only relevant actions shown
     • Inline editor (12h window enforced server-side)
     • Reaction chips below the body (click chip to toggle)
     • "(edited)" indicator
     • Deleted-tombstone state
   Founder ask May 16, 2026. */
function MessageRow({ message, handle, stack, api, viewerId, viewerRole, onUpdated, onDeleted, onReplyInline, onPinToggled }) {
  const m = message;
  const isOwn = m.author_user_id === viewerId;
  const isAdmin = ['master_admin', 'production_manager', 'hr_manager'].includes(viewerRole);
  const isLeader = [
    'master_admin', 'production_manager', 'hr_manager',
    'coo', 'studio_director', 'creative_director',
    'art_director', 'animation_director', 'director',
    'vfx_supervisor', 'cg_supervisor', 'supervisor',
    'technical_director', 'executive_producer',
  ].includes(viewerRole);
  // 12h edit/delete window — keep this in sync with the backend constant.
  const ageMs = Date.now() - new Date(m.created_at).getTime();
  const withinWindow = ageMs < 12 * 3600 * 1000;
  const canEdit = isOwn && withinWindow && !m.deleted_at;
  const canDelete = (isOwn && withinWindow && !m.deleted_at) || (isAdmin && !m.deleted_at);
  const canPin = isLeader && !m.deleted_at;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.body || '');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hover, setHover] = useState(false);

  const togglePin = async () => {
    try {
      await api.post(`/messaging/channels/${encodeURIComponent(m.channel_id)}/messages/${m.id}/pin`);
      onPinToggled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to pin');
    }
  };

  const saveEdit = async () => {
    const next = (draft || '').trim();
    if (!next) return;
    try {
      const r = await api.patch(
        `/messaging/channels/${encodeURIComponent(m.channel_id)}/messages/${m.id}`,
        { body: next }
      );
      onUpdated?.(r.data?.message);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteMsg = async () => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await api.delete(`/messaging/channels/${encodeURIComponent(m.channel_id)}/messages/${m.id}`);
      onDeleted?.(m.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleReaction = async (emoji) => {
    setShowEmojiPicker(false);
    try {
      await api.post(
        `/messaging/channels/${encodeURIComponent(m.channel_id)}/messages/${m.id}/reactions`,
        { emoji }
      );
      // Server emits a WS event back which updates the message in place.
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (m.deleted_at) {
    return (
      <div className={`flex gap-3 px-2 py-1 ${stack ? 'pl-12' : ''}`}>
        {!stack && <div className="w-9 shrink-0" />}
        <div className="text-[12px] text-zinc-600 italic">
          <Trash2 className="w-3 h-3 inline mr-1" />
          Message deleted
        </div>
      </div>
    );
  }

  const reactions = m.reactions || {};
  const reactionEntries = Object.entries(reactions);

  return (
    <div
      className={`group relative flex gap-3 px-2 py-1 hover:bg-zinc-900/40 rounded-md ${stack ? 'pl-12' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setShowEmojiPicker(false); }}
      data-testid={`mc-msg-${m.id}`}
      data-message-id={m.id}
    >
      {!stack && (
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center text-[11px] font-bold text-white shrink-0">
          {(m.author_name || handle).charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {!stack && (
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-semibold text-zinc-100">
              @{handle || m.author_name}
            </span>
            <span className="text-[10px] text-zinc-500">
              {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
            {m.edited_at && (
              <span className="text-[9px] text-zinc-600 italic" title={`Edited ${new Date(m.edited_at).toLocaleString()}`}>(edited)</span>
            )}
            {m.pinned && (
              <span className="text-[9px] text-amber-300 flex items-center gap-0.5 italic"
                    title={`Pinned by @${m.pinned_by_handle || 'admin'}`}>
                <Pin className="w-2.5 h-2.5" /> pinned
              </span>
            )}
          </div>
        )}
        {/* Telegram-style inline parent quote — clicking it scrolls
            to and highlights the parent in the main feed. */}
        {m.parent && (
          <button
            type="button"
            onClick={() => {
              if (!m.parent.id) return;
              const el = document.querySelector(`[data-message-id="${m.parent.id}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('mc-flash-highlight');
                setTimeout(() => el.classList.remove('mc-flash-highlight'), 1600);
              }
            }}
            className="block w-full text-left mb-1 pl-2 border-l-2 border-emerald-500/60 bg-emerald-500/5 hover:bg-emerald-500/10 rounded-r px-2 py-1 transition-colors"
            data-testid={`mc-reply-quote-${m.id}`}
            title="Jump to original message"
          >
            <div className="text-[10px] text-emerald-300 font-semibold">
              ↪ {m.parent.author_name || ('@' + (m.parent.author_handle || 'user'))}
            </div>
            <div className="text-[11px] text-zinc-400 truncate">
              {m.parent.deleted ? <em className="text-zinc-600">(message deleted)</em> : (m.parent.body_snippet || '…')}
            </div>
          </button>
        )}
        {editing ? (
          <div className="mt-1">
            <textarea
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                if (e.key === 'Escape') { setEditing(false); setDraft(m.body || ''); }
              }}
              rows={Math.min(6, draft.split('\n').length + 1)}
              className="w-full bg-zinc-900 border border-indigo-500/60 rounded text-[13px] text-zinc-100 px-2 py-1.5 outline-none resize-none"
              data-testid={`mc-edit-input-${m.id}`}
            />
            <div className="flex items-center gap-1.5 mt-1">
              <button onClick={saveEdit}
                      className="px-2.5 py-0.5 bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] rounded"
                      data-testid={`mc-edit-save-${m.id}`}>
                Save
              </button>
              <button onClick={() => { setEditing(false); setDraft(m.body || ''); }}
                      className="px-2.5 py-0.5 text-zinc-400 hover:text-zinc-200 text-[10px]">
                Cancel
              </button>
              <span className="text-[9px] text-zinc-600 ml-auto">Esc to cancel · Enter to save</span>
            </div>
          </div>
        ) : (
          m.body && (
            <div className="text-[13px] text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">
              {renderBody(m.body)}
            </div>
          )
        )}
        {(m.attachments || []).map((a, i) => (
          <AttachmentPreview key={i} attachment={a} api={api} />
        ))}
        {/* Thread-expand affordance removed (founder ask May 17, 2026):
            replies live inline in the same feed now, so an explicit "N
            replies →" button is no longer needed. */}
        {reactionEntries.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {reactionEntries.map(([emoji, users]) => {
              const youReacted = users.includes(viewerId);
              return (
                <button
                  key={emoji}
                  onClick={() => toggleReaction(emoji)}
                  className={`text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${
                    youReacted
                      ? 'bg-indigo-500/20 border-indigo-500/60 text-indigo-100'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                  }`}
                  title={youReacted ? 'Click to remove your reaction' : 'Add your reaction'}
                  data-testid={`mc-reaction-${m.id}-${emoji}`}
                >
                  {emoji} {users.length}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Hover toolbar (react · edit · delete) */}
      {hover && !editing && (
        <div className="absolute top-0 right-2 -translate-y-1/2 bg-zinc-900 border border-zinc-700 rounded-md flex items-center shadow-lg z-10"
             data-testid={`mc-msg-toolbar-${m.id}`}>
          <button
            onClick={() => setShowEmojiPicker(v => !v)}
            className="px-2 py-1 text-zinc-400 hover:text-amber-300 hover:bg-zinc-800 rounded-l-md"
            title="Add reaction"
            data-testid={`mc-react-btn-${m.id}`}
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
          {/* Reply (Telegram-style inline — sets the parent quote on the
              main composer instead of opening a side panel) */}
          {!m.reply_to_id && (
            <button
              onClick={() => onReplyInline?.(m)}
              className="px-2 py-1 text-zinc-400 hover:text-emerald-300 hover:bg-zinc-800"
              title="Reply"
              data-testid={`mc-reply-btn-${m.id}`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17l-5-5 5-5"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
            </button>
          )}
          {/* Pin — leadership only, top-level messages only */}
          {canPin && !m.reply_to_id && (
            <button
              onClick={togglePin}
              className={`px-2 py-1 hover:bg-zinc-800 ${m.pinned ? 'text-amber-300' : 'text-zinc-400 hover:text-amber-300'}`}
              title={m.pinned ? 'Unpin message' : 'Pin message'}
              data-testid={`mc-pin-btn-${m.id}`}
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setEditing(true); setDraft(m.body || ''); }}
              className="px-2 py-1 text-zinc-400 hover:text-indigo-300 hover:bg-zinc-800"
              title="Edit message"
              data-testid={`mc-edit-btn-${m.id}`}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={deleteMsg}
              className="px-2 py-1 text-zinc-400 hover:text-rose-300 hover:bg-zinc-800 rounded-r-md"
              title={isAdmin && !isOwn ? 'Delete (admin)' : 'Delete (within 12h)'}
              data-testid={`mc-delete-btn-${m.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {showEmojiPicker && (
        <EmojiReactionPicker
          onPick={toggleReaction}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}
    </div>
  );
}

/* ─── Emoji Reaction Picker ────────────────────────────────────────
   Compact, curated set of the emojis VOS teams actually use. Hidden
   behind a button (not a fly-out for every message) to keep the chat
   visually quiet. Click outside or pick any emoji to close. */
const REACTION_EMOJIS = [
  '👍', '👎', '❤️', '🔥', '🎉', '🙏',
  '👀', '💯', '🚀', '✅', '❌', '😂',
  '😍', '😮', '😢', '🤔', '⚡', '🎬',
];
function EmojiReactionPicker({ onPick, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="absolute top-7 right-2 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl p-2 z-20 grid grid-cols-6 gap-1"
         data-testid="mc-emoji-picker">
      {REACTION_EMOJIS.map(e => (
        <button
          key={e}
          onClick={() => onPick(e)}
          className="w-8 h-8 text-lg hover:bg-zinc-800 rounded transition-colors"
          data-testid={`mc-emoji-${e}`}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

/* ─── Channel Topic Inline ─────────────────────────────────────────
   One-line note shown next to the channel name, e.g.:
     #asset-build · "Tag @rig for Rigging, @bg for BG, @props for Props"
   Editable by PM/Admin/Leadership; readable by everyone with channel
   access. Founder ask May 16, 2026. */
function ChannelTopicInline({ topic, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(topic || '');
  useEffect(() => { setDraft(topic || ''); }, [topic]);

  const save = async () => {
    setEditing(false);
    if ((draft || '').trim() === (topic || '').trim()) return;  // no-op
    await onSave?.(draft.trim());
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); save(); }
          if (e.key === 'Escape') { setEditing(false); setDraft(topic || ''); }
        }}
        placeholder="Add a channel topic — e.g. Tag @rig for Rigging, @bg for BG, @props for Props"
        maxLength={280}
        className="bg-zinc-800 border border-indigo-500/60 rounded px-2 py-0.5 text-[11px] text-zinc-200 placeholder-zinc-600 outline-none flex-1 min-w-0 max-w-md"
        data-testid="mc-topic-input"
      />
    );
  }

  if (!topic) {
    if (!canEdit) return null;
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-[10px] text-zinc-600 hover:text-indigo-300 italic flex items-center gap-1"
        data-testid="mc-topic-add"
        title="Add a channel topic"
      >
        <Pin className="w-2.5 h-2.5" />
        Add a topic
      </button>
    );
  }
  return (
    <button
      onClick={() => canEdit && setEditing(true)}
      disabled={!canEdit}
      className={`text-[11px] text-zinc-400 truncate max-w-md text-left flex items-center gap-1 ${canEdit ? 'hover:text-zinc-200 cursor-text' : 'cursor-default'}`}
      title={canEdit ? topic + ' — click to edit' : topic}
      data-testid="mc-topic"
    >
      <Pin className="w-2.5 h-2.5 text-amber-400 shrink-0" />
      <span className="truncate italic">{topic}</span>
    </button>
  );
}



/* ─── Thread Panel ─────────────────────────────────────────────────
   Right-rail panel that opens when the user clicks "N replies" or the
   Reply button on a top-level message. Shows the parent message at the
   top, then every reply below it in chronological order, with a
   composer pinned to the bottom that posts replies to this thread.

   Founder ask May 16, 2026 (Phase 3 part 2). */
function ThreadPanel({ api, channelId, thread, viewerId, viewerRole, onClose, onAppendReply, onMessageUpdated, onMessageDeleted }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll thread to bottom on new replies (parent + replies length).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread?.replies?.length]);

  const sendReply = async () => {
    const text = draft.trim();
    if (!text || !thread?.parent?.id) return;
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post(`/messaging/channels/${encodeURIComponent(channelId)}/messages`, {
        body: text,
        reply_to_id: thread.parent.id,
      });
      if (r.data?.message) onAppendReply?.(r.data.message);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reply');
    }
    setSending(false);
  };

  const parent = thread?.parent;
  if (!parent) return null;
  const parentHandle = parent.author_handle || HANDLE_FROM_EMAIL(parent.author_email || '');

  return (
    <aside
      className="flex flex-col w-80 shrink-0 bg-zinc-900/60 border-l border-zinc-800"
      data-testid="mc-thread-panel"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Thread</div>
          <div className="text-[12px] text-zinc-300">{(thread.replies || []).length} {(thread.replies || []).length === 1 ? 'reply' : 'replies'}</div>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="mc-thread-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {/* Parent message */}
        <div className="border-l-2 border-amber-400/60 pl-3 pb-2 border-b border-zinc-800/50">
          <MessageRow
            message={parent}
            handle={parentHandle}
            stack={false}
            api={api}
            viewerId={viewerId}
            viewerRole={viewerRole}
            onUpdated={onMessageUpdated}
            onDeleted={onMessageDeleted}
            onOpenThread={() => {}}
            onPinToggled={() => {}}
          />
        </div>
        {/* Replies */}
        {(thread.replies || []).map(r => (
          <div key={r.id} className="border-l border-zinc-800 pl-3">
            <MessageRow
              message={r}
              handle={r.author_handle || HANDLE_FROM_EMAIL(r.author_email || '')}
              stack={false}
              api={api}
              viewerId={viewerId}
              viewerRole={viewerRole}
              onUpdated={onMessageUpdated}
              onDeleted={onMessageDeleted}
              onOpenThread={() => {}}
              onPinToggled={() => {}}
            />
          </div>
        ))}
        {(thread.replies || []).length === 0 && (
          <div className="text-[11px] text-zinc-600 italic text-center py-6">
            No replies yet. Be the first to reply.
          </div>
        )}
      </div>

      {/* Reply composer */}
      <div className="border-t border-zinc-800 bg-zinc-950/60 p-3 shrink-0">
        <div className="flex items-end gap-2 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 focus-within:border-indigo-400 transition-colors">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
            }}
            rows={1}
            placeholder="Reply in thread…"
            data-testid="mc-thread-composer"
            className="flex-1 bg-transparent text-[12px] text-zinc-100 placeholder-zinc-600 outline-none resize-none max-h-32"
          />
          <button
            onClick={sendReply}
            disabled={sending || !draft.trim()}
            data-testid="mc-thread-send"
            className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-30 text-white p-1.5 rounded-md"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-[9px] text-zinc-600 mt-1">Enter to send · Shift+Enter for newline</div>
      </div>
    </aside>
  );
}

/* ─── Pinned Panel ─────────────────────────────────────────────────
   Right-rail panel listing every pinned message in the current channel,
   newest pinned first. Click any row to jump to (and highlight) the
   message in the main feed. Founder ask May 16, 2026. */
function PinnedPanel({ pinned, onClose, onJump }) {
  return (
    <aside
      className="hidden xl:flex flex-col w-72 shrink-0 bg-zinc-900/40 border-l border-zinc-800 overflow-y-auto"
      data-testid="mc-pinned-panel"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pin className="w-3.5 h-3.5 text-amber-300" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Pinned</div>
            <div className="text-[12px] text-zinc-300">{pinned.length} message{pinned.length === 1 ? '' : 's'}</div>
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="mc-pinned-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-2">
        {pinned.length === 0 && (
          <div className="text-[11px] text-zinc-600 italic px-2 py-4">No pinned messages.</div>
        )}
        {pinned.map(p => (
          <button
            key={p.id}
            onClick={() => onJump?.(p.id)}
            className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-amber-500/40 rounded-md px-3 py-2 transition-colors"
            data-testid={`mc-pinned-${p.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-indigo-300 font-medium">
                @{p.author_handle || HANDLE_FROM_EMAIL(p.author_email || '')}
              </span>
              <span className="text-[9px] text-zinc-500">
                pinned by @{p.pinned_by_handle || '—'}
              </span>
            </div>
            <div className="text-[12px] text-zinc-300 mt-1 line-clamp-3 whitespace-pre-wrap break-words">
              {p.body}
            </div>
            <div className="text-[9px] text-zinc-600 mt-1">
              {new Date(p.created_at).toLocaleString()}
            </div>
          </button>
        ))}
      </div>
    </aside>
  );
}
